<template>
  <div class="brand-home">
    <main>
      <!-- HERO SECTION -->
      <section class="hero">
        <h1>AI-Powered Systematic Learning</h1>
        <p class="subtitle">From scattered Q&A to structured learning journeys—planned, facilitated, and atomized by AI</p>
        <p class="hero-description">Traditional learning tools only answer questions you already know to ask. <span v-html="brandName"></span> is different—AI designs your complete learning map, breaks complex topics into digestible pieces, and guides you step-by-step through a coherent knowledge journey. True systematic learning, powered by artificial intelligence.</p>
        <div class="cta-buttons">
          <router-link to="/plaza" class="cta-button primary">Start Learning</router-link>
          <router-link to="/plaza" class="cta-button secondary">Explore Decks</router-link>
        </div>
      </section>

      <!-- THE KNOWIT DIFFERENCE -->
      <section class="difference-section">
        <h2>The <span v-html="brandName"></span> Difference</h2>
        <div class="difference-content">
          <div class="problem-box">
            <h3>The Problem We Solve</h3>
            <p>Traditional AI tools only answer questions you already know to ask. But real learning isn't random Q&A—it's a structured journey. You need someone (or something) to map out what you don't know you don't know, and guide you through it systematically.</p>
          </div>
          <div class="approach-box">
            <h3>Our Three AI Pillars</h3>
            <p><span v-html="brandName"></span> transforms how you learn by leveraging AI in three fundamental ways:</p>
            <div class="principle-grid">
              <div class="principle-card">
                <svg class="principle-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill="currentColor"/>
                </svg>
                <h4>AI-Curated Learning Maps</h4>
                <p>AI doesn't just answer—it plans. Our AI designs complete learning journeys, mapping out a chain of interconnected concepts that build true systematic understanding.</p>
              </div>
              <div class="principle-card">
                <svg class="principle-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="7" height="7" rx="1" fill="currentColor"/>
                  <rect x="14" y="3" width="7" height="7" rx="1" fill="currentColor"/>
                  <rect x="3" y="14" width="7" height="7" rx="1" fill="currentColor"/>
                  <rect x="14" y="14" width="7" height="7" rx="1" fill="currentColor"/>
                </svg>
                <h4>AI-Atomized Content</h4>
                <p>Complex topics are intelligently broken into bite-sized, swipeable cards—text, visuals, quizzes, and exercises—making deep learning feel effortless and engaging.</p>
              </div>
              <div class="principle-card">
                <svg class="principle-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" fill="currentColor"/>
                  <circle cx="12" cy="10" r="2" fill="currentColor"/>
                  <circle cx="7" cy="10" r="1.5" fill="currentColor"/>
                  <circle cx="17" cy="10" r="1.5" fill="currentColor"/>
                </svg>
                <h4>AI-Facilitated Experience</h4>
                <p>AI tutors accompany your journey—answering questions, providing explanations, adapting to your pace, and ensuring you truly grasp each concept before moving forward.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- HOW IT WORKS -->
      <section class="how-it-works">
        <h2>How It Works</h2>
        <div class="steps-grid">
          <div class="step-card">
            <div class="step-number">1</div>
            <h3>Choose Your Learning Goal</h3>
            <p>Select a topic or subscribe to a curated deck. AI analyzes the subject matter and designs a personalized learning map—a structured sequence of concepts that build upon each other.</p>
          </div>
          <div class="step-card">
            <div class="step-number">2</div>
            <h3>AI Breaks It Down</h3>
            <p>Complex knowledge is atomized into digestible cards—each containing focused content, visuals, quizzes, or exercises. No overwhelming walls of text—just clear, engaging micro-lessons.</p>
          </div>
          <div class="step-card">
            <div class="step-number">3</div>
            <h3>Learn at Your Pace</h3>
            <p>Swipe through cards, interact with content, and test your understanding. Content arrives in your inbox progressively, making learning a natural daily habit rather than a daunting task.</p>
          </div>
          <div class="step-card">
            <div class="step-number">4</div>
            <h3>AI Guides You Forward</h3>
            <p>Stuck on a concept? AI tutors are there to explain, elaborate, and adapt. Ask questions anytime and get personalized guidance that ensures you truly master each step of your journey.</p>
          </div>
        </div>
      </section>

      <!-- WHY KNOWIT -->
      <section class="why-knowit">
        <h2>Why <span v-html="brandName"></span>?</h2>
        <div class="benefits-grid">
          <div class="benefit-card">
            <svg class="benefit-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill="currentColor"/>
            </svg>
            <h3>Beyond Q&A</h3>
            <p>Traditional AI only answers what you ask. <span v-html="brandName"></span> maps out what you don't know you need to learn, creating a complete knowledge journey.</p>
          </div>
          <div class="benefit-card">
            <svg class="benefit-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z" fill="currentColor"/>
              <circle cx="8" cy="7" r="2" fill="currentColor"/>
              <circle cx="12" cy="12" r="2" fill="currentColor"/>
              <circle cx="16" cy="17" r="2" fill="currentColor"/>
            </svg>
            <h3>Structured Progression</h3>
            <p>Each concept builds on the previous one. AI ensures you master fundamentals before advancing, creating lasting understanding instead of fragmented facts.</p>
          </div>
          <div class="benefit-card">
            <svg class="benefit-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="7" height="7" rx="1" fill="currentColor"/>
              <rect x="14" y="3" width="7" height="7" rx="1" fill="currentColor"/>
              <rect x="3" y="14" width="7" height="7" rx="1" fill="currentColor"/>
              <rect x="14" y="14" width="7" height="7" rx="1" fill="currentColor"/>
            </svg>
            <h3>Effortless Depth</h3>
            <p>Complex topics atomized into engaging cards—text, visuals, quizzes—making deep learning feel natural. Sophisticated content, simple experience.</p>
          </div>
          <div class="benefit-card">
            <svg class="benefit-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" fill="currentColor"/>
              <circle cx="12" cy="10" r="2" fill="currentColor"/>
              <circle cx="7" cy="10" r="1.5" fill="currentColor"/>
              <circle cx="17" cy="10" r="1.5" fill="currentColor"/>
            </svg>
            <h3>Always-On AI Tutor</h3>
            <p>Never stuck alone. AI tutors explain concepts, answer questions, and adapt to your pace—personalized guidance throughout your learning journey.</p>
          </div>
          <div class="benefit-card">
            <svg class="benefit-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
            </svg>
            <h3>Habit-Forming Design</h3>
            <p>Learning delivered progressively to your inbox, building knowledge as a daily habit. Consistent progress without overwhelm or burnout.</p>
          </div>
        </div>
      </section>

      <!-- PERFECT FOR -->
      <section class="perfect-for">
        <h2>Perfect For</h2>
        <div class="audience-grid">
          <div class="audience-card">
            <h3>Students</h3>
            <p>Who need AI to map out what they should learn, not just answer what they ask</p>
          </div>
          <div class="audience-card">
            <h3>Professionals</h3>
            <p>Upskilling systematically with AI-guided learning paths in new domains</p>
          </div>
          <div class="audience-card">
            <h3>Self-Learners</h3>
            <p>Who want structured progression instead of random YouTube tutorials</p>
          </div>
          <div class="audience-card">
            <h3>Anyone</h3>
            <p>Ready to move beyond Q&A chatbots to true systematic knowledge building</p>
          </div>
        </div>
      </section>

      <!-- NEWS & UPDATES -->
      <!-- <BrandNews :userId="newsUserId" :maxItems="5" /> -->

      <!-- CURRENTLY AVAILABLE -->
      <section class="currently-available">
        <div class="availability-content">
          <div class="availability-badge">
            <svg class="badge-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L15 8.5L22 9.5L17 14.5L18 21.5L12 18L6 21.5L7 14.5L2 9.5L9 8.5L12 2Z" fill="white"/>
              <path d="M12 4L9 9L4 10L8 14L7 19L12 16L17 19L16 14L20 10L15 9L12 4Z" fill="currentColor"/>
            </svg>
            Early Access
          </div>
          <h2><span v-html="brandName"></span> Decks</h2>
          <p class="availability-description">Experience AI-powered systematic learning today. Browse curated decks where AI has mapped out complete learning journeys—from fundamentals to mastery. Each deck features atomized content, interactive exercises, and AI tutoring support.</p>
          <p class="coming-soon">Coming soon: custom learning path generation, advanced AI tutoring, progress analytics, and collaborative learning features.</p>
        </div>
      </section>

      <!-- FINAL CTA -->
      <section class="final-cta">
        <h2>Learn Systematically with AI</h2>
        <p class="tagline">"Where AI Plans Your Journey, Breaks Down Complexity, and Guides Every Step"</p>
        <div class="cta-buttons">
          <router-link to="/plaza" class="cta-button primary">Start Learning</router-link>
          <router-link to="/plaza" class="cta-button secondary">Explore Decks</router-link>
        </div>
      </section>
    </main>

    <footer class="brand-footer">
      <div class="footer-links">
        <router-link to="/privacy-policy">Privacy Policy</router-link>
        <router-link to="/terms-of-service">Terms of Service</router-link>
        <router-link to="/subscription-terms">Subscription Terms</router-link>
      </div>
      <p>&copy; {{ new Date().getFullYear() }} EXTRACURRICULAR ACTIVITY EXCELLENCE INTERNATIONAL PTE. LTD. All rights reserved.</p>
    </footer>
  </div>
</template>

<style scoped>
.brand-home {
  min-height: 100vh;
  background-color: #ffffff;
}

/* HERO SECTION */
.hero {
  text-align: center;
  padding: 100px 20px;
  background: linear-gradient(135deg, #409EFF0D 0%, #409EFF1A 100%);
}

.hero h1 {
  font-size: 52px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 20px;
  max-width: 900px;
  margin: 0 auto 20px;
  line-height: 1.2;
}

.subtitle {
  font-size: 24px;
  font-style: italic;
  color: #606266;
  margin-bottom: 30px;
}

.hero-description {
  font-size: 18px;
  color: #606266;
  max-width: 800px;
  margin: 0 auto 40px;
  line-height: 1.7;
}

.cta-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-button {
  display: inline-block;
  padding: 15px 40px;
  border-radius: 4px;
  text-decoration: none;
  font-size: 18px;
  font-weight: 600;
  transition: all 0.3s;
}

.cta-button.primary {
  background-color: #409EFF;
  color: white;
}

.cta-button.primary:hover {
  background-color: #66b1ff;
  transform: translateY(-2px);
}

.cta-button.secondary {
  background-color: white;
  color: #409EFF;
  border: 2px solid #409EFF;
}

.cta-button.secondary:hover {
  background-color: #ecf5ff;
  transform: translateY(-2px);
}

/* THE KNOWIT DIFFERENCE */
.difference-section {
  padding: 80px 20px;
  max-width: 1200px;
  margin: 0 auto;
  background-color: #fafafa;
}

.difference-section h2 {
  text-align: center;
  font-size: 40px;
  color: #303133;
  margin-bottom: 50px;
}

.difference-content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.problem-box,
.approach-box {
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.problem-box h3,
.approach-box h3 {
  font-size: 28px;
  color: #303133;
  margin-bottom: 16px;
}

.problem-box p,
.approach-box > p {
  font-size: 16px;
  color: #606266;
  line-height: 1.7;
  margin-bottom: 24px;
}

.principle-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-top: 24px;
}

.principle-card {
  background: #f8f9fa;
  padding: 24px;
  border-radius: 8px;
  border: 2px solid #409EFF20;
}

.principle-icon {
  width: 36px;
  height: 36px;
  color: #409EFF;
  margin-bottom: 12px;
}

.principle-card h4 {
  font-size: 20px;
  color: #303133;
  margin-bottom: 12px;
}

.principle-card p {
  font-size: 15px;
  color: #606266;
  line-height: 1.6;
}

/* HOW IT WORKS */
.how-it-works {
  padding: 80px 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.how-it-works h2 {
  text-align: center;
  font-size: 40px;
  color: #303133;
  margin-bottom: 60px;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
}

.step-card {
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  position: relative;
}

.step-card:hover {
  transform: translateY(-5px);
}

.step-number {
  position: absolute;
  top: -15px;
  left: 20px;
  width: 40px;
  height: 40px;
  background: #409EFF;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
}

.step-card h3 {
  font-size: 22px;
  color: #303133;
  margin-bottom: 12px;
  margin-top: 10px;
}

.step-card p {
  color: #606266;
  line-height: 1.7;
  font-size: 15px;
}

/* WHY KNOWIT */
.why-knowit {
  padding: 80px 20px;
  max-width: 1200px;
  margin: 0 auto;
  background: linear-gradient(135deg, #409EFF0D 0%, #409EFF1A 100%);
}

.why-knowit h2 {
  text-align: center;
  font-size: 40px;
  color: #303133;
  margin-bottom: 60px;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.benefit-card {
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  text-align: center;
}

.benefit-card:hover {
  transform: translateY(-5px);
}

.benefit-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 16px;
  color: #409EFF;
}

.benefit-card h3 {
  font-size: 22px;
  color: #303133;
  margin-bottom: 12px;
}

.benefit-card p {
  color: #606266;
  line-height: 1.6;
  font-size: 15px;
}

/* PERFECT FOR */
.perfect-for {
  padding: 80px 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.perfect-for h2 {
  text-align: center;
  font-size: 40px;
  color: #303133;
  margin-bottom: 50px;
}

.audience-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
}

.audience-card {
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  text-align: center;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.audience-card:hover {
  border-color: #409EFF;
  transform: translateY(-3px);
}

.audience-card h3 {
  font-size: 24px;
  color: #409EFF;
  margin-bottom: 12px;
}

.audience-card p {
  color: #606266;
  line-height: 1.6;
  font-size: 15px;
}

/* CURRENTLY AVAILABLE */
.currently-available {
  padding: 80px 20px;
  background: linear-gradient(135deg, #67C23A10 0%, #67C23A20 100%);
}

.availability-content {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.availability-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #67C23A;
  color: white;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 20px;
}

.badge-icon {
  width: 18px;
  height: 18px;
}

.availability-content h2 {
  font-size: 40px;
  color: #303133;
  margin-bottom: 20px;
}

.availability-description {
  font-size: 18px;
  color: #606266;
  line-height: 1.7;
  margin-bottom: 20px;
}

.coming-soon {
  font-size: 15px;
  color: #909399;
  font-style: italic;
  line-height: 1.6;
}

/* FINAL CTA */
.final-cta {
  padding: 100px 20px;
  text-align: center;
  background: linear-gradient(135deg, #409EFF20 0%, #409EFF30 100%);
}

.final-cta h2 {
  font-size: 44px;
  color: #303133;
  margin-bottom: 20px;
}

.tagline {
  font-size: 20px;
  font-style: italic;
  color: #606266;
  margin-bottom: 40px;
}

/* FOOTER */
.brand-footer {
  text-align: center;
  padding: 30px 20px;
  background-color: #f5f7fa;
  color: #909399;
  font-size: 14px;
}

.footer-links {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-bottom: 12px;
}

.footer-links a {
  color: #606266;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.3s;
}

.footer-links a:hover {
  color: #409EFF;
}

/* RESPONSIVE */
@media (max-width: 768px) {
  .hero h1 {
    font-size: 36px;
  }

  .subtitle {
    font-size: 18px;
  }

  .hero-description {
    font-size: 16px;
  }

  .difference-section h2,
  .how-it-works h2,
  .why-knowit h2,
  .perfect-for h2,
  .availability-content h2,
  .final-cta h2 {
    font-size: 32px;
  }

  .steps-grid,
  .benefits-grid,
  .audience-grid {
    grid-template-columns: 1fr;
  }

  .principle-grid {
    grid-template-columns: 1fr;
  }

  .cta-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .cta-button {
    width: 100%;
    max-width: 300px;
    margin: 0 auto;
  }

  .footer-links {
    flex-direction: column;
    gap: 12px;
  }
}
</style>

<script>
import BrandNews from './News.vue'
import branding from '@/config/branding'

export default {
  name: 'BrandHome',
  components: {
    BrandNews
  },
  data() {
    return {
      newsUserId: '68b11e1d94c784e0568b4590',
      branding
    }
  },
  computed: {
    brandName() {
      if (this.branding.name === 'LearningFirst') {
        return 'LearningFirst'
      }
      return 'Knowit<sup class="tm">™</sup> Learning'
    },
    brandNamePlain() {
      if (this.branding.name === 'LearningFirst') {
        return 'LearningFirst'
      }
      return 'Knowit™ Learning'
    },
  }
}
</script>
